import {
    useStyles,
    pxToRem,
    ProfileNavigation,
    Dropdown,
} from '@archipro-design/aria';

import { Link, NavLink, useLocation } from '@remix-run/react';
import type { CategoryNavigationProp } from './CategoryNavigation';
import * as S from './CategoryNavigation.mobile.style';
import classNames from 'classnames';
import type { NavItem } from '../../type';
import { useReducer, useRef } from 'react';
import { useClickAway, useIsomorphicLayoutEffect } from 'ahooks';

import { AngleDown } from '@archipro-design/icons';
import React from 'react';
import { useHydrated } from 'remix-utils';

interface SubCategoryNavigationProps {
    categories: NavItem[];
}

type Actions = 'toggle-menu' | 'close-menu';

export interface MobileSubCategoryNavigationState {
    current: 'opened' | 'closed';
    menuIsOpen: boolean;
}
const reducer = (
    state: MobileSubCategoryNavigationState,
    action: { type: Actions }
): MobileSubCategoryNavigationState => {
    switch (action.type) {
        case 'toggle-menu':
            const next = state.current === 'opened' ? 'closed' : 'opened';
            return {
                current: next,
                menuIsOpen: !state.menuIsOpen,
            };
        case 'close-menu':
            return {
                current: 'closed',
                menuIsOpen: false,
            };
    }
};

const SubCategoryNavigation = ({ categories }: SubCategoryNavigationProps) => {
    const location = useLocation();
    const activeLink = location.pathname;
    const ref = useRef<HTMLDivElement>(null);
    const [state, dispatch] = useReducer(reducer, {
        current: 'closed',
        menuIsOpen: false,
    });
    const { css } = useStyles({ toggleState: state });
    const activeCategory = categories.find(
        (navItem) =>
            navItem.link !== '/articles' && activeLink.includes(navItem.link)
    );
    const subcategories = activeCategory?.children ?? [];
    const activeSubcategory = subcategories.find(
        (navItem) => navItem.link === activeLink
    );
    const listedSubcategories = subcategories.filter(
        (navItem) => !(navItem.link === activeLink)
    );

    useClickAway(() => dispatch({ type: 'close-menu' }), ref);

    if (
        !activeCategory ||
        subcategories.length === 0 ||
        !activeSubcategory ||
        listedSubcategories.length === 0
    ) {
        return null;
    }

    const dropdownItems = listedSubcategories.map((cat, i) => ({
        key: `articles-SubCategoryNavigation__DropdownItem-${i}`,
        className: classNames(
            'articles-SubCategoryNavigation__DropdownItem',
            css(S.DropdownItem)
        ),
        content: (
            <NavLink
                onClick={() => dispatch({ type: 'close-menu' })}
                className={css(S.NavLink)}
                to={cat.link}
                end={true}
                preventScrollReset={true}
            >
                {cat.title}
            </NavLink>
        ),
    }));

    const dropdownVariables = {
        backgroundColor: 'transparent',
        backgroundColorHover: 'transparent',
        listBoxShadow: 'none',
        openBelowContainerBorderRadius: '0',
        belowListBorderRadius: '0',
        ...(state.menuIsOpen && {
            backgroundColor: 'white',
            backgroundColorHover: 'white',
            borderColorFocus: 'white',
            borderColor: 'white',
            listBorderColor: 'white',
            listItemBackgroundColorActive: 'white',
            selectedItemBackgroundColor: 'white',
        }),
    };

    return (
        <>
            {state.menuIsOpen && (
                <div
                    className={classNames(
                        'articles-MobileNavigation__Overlay',
                        css(S.CloseOverlay)
                    )}
                />
            )}

            <Dropdown
                className={classNames(css(S.DropdownRoot))}
                ref={ref}
                items={dropdownItems}
                highlightFirstItemOnOpen={false}
                highlightedIndex={-1}
                triggerButton={{
                    className: classNames(
                        'articles-SubCategoryNavigation__Trigger',
                        css(S.DropdownTrigger)
                    ),
                    content: activeSubcategory.title,
                }}
                onClick={() => dispatch({ type: 'toggle-menu' })}
                defaultOpen={state.menuIsOpen}
                variables={dropdownVariables}
                offset={[0, -1]}
                align={'center'}
                fluid={true}
                toggleIndicator={
                    <AngleDown
                        className={classNames(
                            'articles-SubCategoryNavigation__TriggerIcon',
                            css(S.ToggleChevron)
                        )}
                        size={'1x'}
                    />
                }
            />
        </>
    );
};

const CategoryNavigationMobile: React.FC<CategoryNavigationProp> = ({
    navItems,
    activeRoute: defaultActiveRoute,
}) => {
    const { css } = useStyles();
    const [activeRoute, setActiveRoute] = React.useState(defaultActiveRoute);
    const navRef = React.useRef<HTMLDivElement>(null);
    const hydrated = useHydrated();

    React.useEffect(() => {
        setActiveRoute(defaultActiveRoute);
    }, [defaultActiveRoute]);

    useIsomorphicLayoutEffect(() => {
        if (activeRoute && hydrated) {
            const link = navRef.current?.querySelector(
                `[href="${activeRoute}"]`
            );
            if (!link) {
                return;
            }
            const linkContainer = link.parentElement;
            if (!linkContainer) {
                return;
            }
            const linkBounds = link.getBoundingClientRect();
            const containerBounds = linkContainer.getBoundingClientRect();

            const maxScrollX = linkContainer.scrollWidth;
            const currentScrollX = linkContainer.scrollLeft;
            const expectedScrollX =
                currentScrollX +
                linkBounds.left -
                containerBounds.left -
                containerBounds.width / 2 +
                linkBounds.width / 2;
            const scrollX = Math.max(0, Math.min(maxScrollX, expectedScrollX));

            linkContainer.scrollTo({
                left: scrollX,
                top: linkContainer.scrollTop,
                behavior: 'smooth',
            });
        }
    }, [activeRoute, navRef, hydrated]);

    if (!activeRoute) {
        return null;
    }

    return (
        <>
            <div className={css(S.MobileNav)}>
                <ProfileNavigation
                    ref={navRef}
                    variables={{ gap: pxToRem(8) }}
                    items={navItems.map((item) => ({
                        children: item.title,
                        as: Link,
                        to: item.link,
                        selected:
                            item.link === activeRoute ||
                            (item.link !== '/articles' &&
                                activeRoute.includes(item.link)),
                        key: item.link,
                        preventScrollReset: true,
                        onClick: () => {
                            setActiveRoute(item.link);
                        },
                    }))}
                />
            </div>
            <SubCategoryNavigation categories={navItems} />
        </>
    );
};

export default CategoryNavigationMobile;
